import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Flex, Spinner } from '@radix-ui/themes';
import { parseISO, getTime, format, startOfWeek } from 'date-fns';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

import SectionCard from '../../../../shared/SectionCard/SectionCard.jsx';
import { useReportsConversationTrends } from '../data.js';
import { useCampaignFilter } from '../../hooks/useCampaignFilter.js';
import styles from './ConversationTrends.module.css';

const CHART_COLOR = '#5c83fa';

const WEEKLY_THRESHOLD = 60;

const parseSeriesData = series =>
  series.map(([date, count]) => [getTime(parseISO(date)), count]);

const aggregateByWeek = dailyData => {
  const weeks = new Map();
  dailyData.forEach(([ts, count]) => {
    const weekStart = getTime(startOfWeek(ts, { weekStartsOn: 1 }));
    weeks.set(weekStart, (weeks.get(weekStart) || 0) + count);
  });
  return Array.from(weeks.entries()).sort((a, b) => a[0] - b[0]);
};

const computeStats = data => {
  if (!data || data.length === 0) return null;

  const total = data.reduce((sum, d) => sum + d[1], 0);
  const avg = (total / data.length).toFixed(1);
  const peak = data.reduce((best, d) => (d[1] > best[1] ? d : best), data[0]);
  const peakLabel = format(peak[0], 'MMM d');

  return { total, avg, peakLabel, peakValue: peak[1] };
};

const buildChartOptions = (seriesData, { isWeekly = false } = {}) => ({
  chart: {
    type: 'areaspline',
    height: 180,
    backgroundColor: 'transparent',
    style: { fontFamily: 'inherit' },
    spacing: [10, 0, 10, 0],
  },
  title: { text: null },
  legend: { enabled: false },
  credits: { enabled: false },
  exporting: { enabled: false },
  xAxis: {
    type: 'datetime',
    lineColor: '#E8E8EC',
    tickLength: 0,
    labels: {
      style: { color: '#8B8D98', fontSize: '10px' },
      format: '{value:%b %d}',
    },
    gridLineWidth: 0,
  },
  yAxis: {
    title: { text: null },
    gridLineColor: '#E8E8EC',
    gridLineDashStyle: 'Dash',
    min: 0,
    allowDecimals: false,
    labels: {
      style: { color: '#8B8D98', fontSize: '10px' },
    },
  },
  tooltip: {
    backgroundColor: '#1C2024',
    borderColor: '#1C2024',
    borderRadius: 10,
    shadow: false,
    style: { color: '#FFFFFF', fontSize: '13px' },
    formatter() {
      const dateLabel = Highcharts.dateFormat('%b %d, %Y', this.x);
      const prefix = isWeekly ? 'Week of ' : '';
      return `${prefix}${dateLabel}<br/><span style="font-size:15px;font-weight:700">${this.y}</span> conversations`;
    },
  },
  plotOptions: {
    areaspline: {
      color: CHART_COLOR,
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, `${CHART_COLOR}1F`],
          [1, `${CHART_COLOR}03`],
        ],
      },
      lineWidth: 2.5,
      marker: {
        enabled: seriesData.length < 30,
        fillColor: '#FFFFFF',
        lineWidth: 2,
        lineColor: CHART_COLOR,
        radius: 3,
        symbol: 'circle',
        states: { hover: { enabled: true, radius: 5, lineWidth: 2 } },
      },
    },
  },
  series: [{ name: 'Conversations', data: seriesData }],
});

export const ConversationTrends = ({ startDate, endDate }) => {
  const { t } = useTranslation('reports');
  const { campaignId } = useCampaignFilter();
  const { data, isLoading } = useReportsConversationTrends({
    campaignId,
    startDate,
    endDate,
  });

  const dailyData = useMemo(() => parseSeriesData(data.series), [data.series]);
  const stats = useMemo(() => computeStats(dailyData), [dailyData]);
  const isWeekly = dailyData.length > WEEKLY_THRESHOLD;
  const chartData = useMemo(
    () => (isWeekly ? aggregateByWeek(dailyData) : dailyData),
    [dailyData, isWeekly],
  );
  const chartOptions = useMemo(
    () => buildChartOptions(chartData, { isWeekly }),
    [chartData, isWeekly],
  );

  const header = (
    <Flex align="center" gap="2">
      <i
        className="fa-regular fa-chart-line"
        style={{ color: 'var(--color-purple)' }}
      />
      <Heading size="4" m="0">
        {t('summary.ai.trends')}
      </Heading>
    </Flex>
  );

  if (isLoading) {
    return (
      <SectionCard headerContent={header}>
        <Flex align="center" justify="center" py="6">
          <Spinner size="3" />
        </Flex>
      </SectionCard>
    );
  }

  if (dailyData.length === 0) {
    return (
      <SectionCard headerContent={header}>
        <div className={styles.emptyState}>
          <i className={`fa-regular fa-chart-line ${styles.emptyIcon}`} />
          <p className={styles.emptyText}>{t('summary.ai.noTrends')}</p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard headerContent={header}>
      <div className={styles.container}>
        {stats && (
          <div className={styles.statsRow}>
            <div className={styles.stat}>
              <span className={styles.statValue}>
                {stats.total.toLocaleString()}
              </span>
              <span className={styles.statLabel}>
                {t('summary.ai.totalConversations')}
              </span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <span className={styles.statValue}>{stats.avg}</span>
              <span className={styles.statLabel}>
                {t('summary.ai.dailyAvg')}
              </span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <span className={styles.statValue}>{stats.peakLabel}</span>
              <span className={styles.statLabel}>
                {t('summary.ai.peakDay')}
              </span>
            </div>
          </div>
        )}

        <div className={styles.chartWrapper}>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>
      </div>
    </SectionCard>
  );
};
